import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, CheckCircle2, TrendingUp, MapPin, Calendar, Quote, Info,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import SEOHead from '@/components/marketing/SEOHead';
import CTASection from '@/components/marketing/CTASection';
import DecorBackground from '@/components/marketing/DecorBackground';
import { Button } from '@/components/ui/button';
import { CASE_STUDIES } from '@/data/case-studies-content';
import { FORMATS } from '@/data/site-content';
import { breadcrumbJsonLd, graph } from '@/lib/jsonld';
import { SITE_URL } from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

function caseStudyJsonLd(c) {
  return {
    '@type': 'Article',
    '@id': `${SITE_URL}/case-studies/${c.slug}#article`,
    headline: c.title,
    description: c.short,
    image: c.heroImage,
    articleSection: 'Case Study',
    inLanguage: 'en',
    about: c.industry,
    author: {
      '@type': 'Organization',
      name: 'Ads Dashbound',
      url: SITE_URL,
    },
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/case-studies/${c.slug}`,
    },
  };
}

export default function CaseStudyDetail() {
  const reduced = useReducedMotion();
  const { slug } = useParams();
  const c = CASE_STUDIES.find((x) => x.slug === slug);
  if (!c) return <Navigate to="/case-studies" replace />;
  const Icon = c.icon;
  const stg = reduced ? {} : stagger;
  const up = reduced ? {} : fadeUp;
  const initial = reduced ? false : 'hidden';

  const recommendedFormats = (c.formats || [])
    .map((s) => FORMATS.find((f) => f.slug === s))
    .filter(Boolean);

  const others = CASE_STUDIES.filter((x) => x.slug !== c.slug);

  const jsonLdNodes = [
    breadcrumbJsonLd([
      { label: 'Home', path: '/' },
      { label: 'Case Studies', path: '/case-studies' },
      { label: c.title },
    ]),
    caseStudyJsonLd(c),
  ];

  return (
    <>
      <SEOHead path={`/case-studies/${c.slug}`} jsonLd={graph(...jsonLdNodes)} />

      {/* Hero */}
      <section
        className="relative bg-[#050B17] text-white isolate overflow-hidden"
        data-testid={`case-study-${c.slug}-hero`}
      >
        <div className="absolute inset-0">
          <img src={c.heroImage} alt={c.imageCaption} className="absolute inset-0 h-full w-full object-cover" />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(5,11,23,0.55) 0%, rgba(5,11,23,0.85) 60%, rgba(5,11,23,1) 100%)',
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 mix-blend-screen opacity-40"
            style={{
              background: `radial-gradient(circle at 78% 20%, ${c.accent}33, transparent 60%), radial-gradient(circle at 20% 80%, rgba(124,58,237,0.25), transparent 65%)`,
            }}
          />
        </div>
        <DecorBackground variant="hero" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24">
          <nav className="text-[12.5px] font-medium text-slate-400 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="mx-2 text-slate-600">/</span>
            <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
            <span className="mx-2 text-slate-600">/</span>
            <span className="text-slate-300">{c.title}</span>
          </nav>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7DDCFF] backdrop-blur-md"
                data-testid={`case-study-${c.slug}-eyebrow`}
              >
                {c.eyebrow}
              </span>
              <h1 className="font-display mt-5 text-[30px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.025em] leading-[1.08] text-white">
                {c.title}
              </h1>
              <p className="mt-5 text-[15.5px] sm:text-[16.5px] text-slate-300/90 leading-[1.7] max-w-2xl">
                {c.short}
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-medium text-slate-200 bg-white/[0.05] border border-white/10">
                  <Calendar className="h-3.5 w-3.5 text-[#7DDCFF]" /> {c.duration}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-medium text-slate-200 bg-white/[0.05] border border-white/10">
                  <MapPin className="h-3.5 w-3.5 text-[#A78BFA]" /> {c.geos.join(', ')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-medium text-slate-200 bg-white/[0.05] border border-white/10">
                  <TrendingUp className="h-3.5 w-3.5 text-[#34D399]" /> {c.industry}
                </span>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button
                  asChild
                  data-testid={`case-study-${c.slug}-apply`}
                  className="btn-primary-gradient text-white rounded-xl px-6 py-5 text-[14px] font-semibold border-0"
                >
                  <Link to="/apply" className="inline-flex items-center gap-2">
                    Apply as Advertiser <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl px-5 py-5 text-[14px] font-semibold transition-colors backdrop-blur-md"
                >
                  <Link to="/case-studies" className="inline-flex items-center gap-2">
                    All case studies
                  </Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div
                className="
                  relative rounded-2xl overflow-hidden h-[280px]
                  border border-[#7DDCFF]/25
                  backdrop-blur-xl
                  shadow-[0_30px_80px_rgba(0,0,0,0.55)]
                "
                aria-hidden="true"
              >
                <img src={c.heroImage} alt={c.imageCaption} className="absolute inset-0 h-full w-full object-cover" />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(5,11,23,0.25) 0%, rgba(5,11,23,0.5) 100%)',
                  }}
                />
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(34,211,238,0.65), rgba(124,58,237,0.65), transparent)',
                  }}
                />
                <span
                  className="absolute top-4 left-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border backdrop-blur-md"
                  style={{
                    background: `linear-gradient(180deg, ${c.accent}33, ${c.accent}10)`,
                    borderColor: `${c.accent}66`,
                    color: c.accent,
                    boxShadow: `0 0 22px ${c.accent}55`,
                  }}
                >
                  <Icon className="h-5 w-5" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="relative bg-[#06101F] text-white isolate overflow-hidden border-y border-white/5">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start gap-3 text-[12px] text-slate-400 leading-relaxed">
            <Info className="h-3.5 w-3.5 mt-0.5 text-[#7DDCFF] shrink-0" />
            <p>
              Illustrative composite. Metrics reflect patterns observed across advertiser launches in this vertical and
              are not tied to a single named client. Past performance is not a guarantee of future results.
            </p>
          </div>
        </div>
      </section>

      {/* Metrics strip */}
      <section className="relative bg-[#050B17] text-white isolate overflow-hidden" data-testid={`case-study-${c.slug}-metrics`}>
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
              Headline numbers
            </span>
            <h2 className="font-display mt-4 text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white">
              What changed across the campaign
            </h2>
          </div>
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {c.metrics.map((m) => (
              <motion.div
                key={m.label}
                variants={up}
                className="
                  relative rounded-2xl
                  bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                  border border-white/10
                  backdrop-blur-xl
                  p-6 h-full overflow-hidden
                "
              >
                <div
                  aria-hidden="true"
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(34,211,238,0.55), rgba(124,58,237,0.55), transparent)',
                  }}
                />
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {m.label}
                </span>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-mono text-[12px] text-slate-500 line-through">{m.from}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-[#22D3EE]" />
                  <span className="font-display text-[22px] font-bold text-white">{m.to}</span>
                </div>
                <p className="mt-2 text-[12px] text-slate-400 leading-snug">{m.note}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Challenge + Approach + Quote */}
      <section className="relative bg-[#06101F] text-white isolate overflow-hidden">
        <DecorBackground variant="section" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-12">
            {/* Summary */}
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7DDCFF] backdrop-blur-md">
                Context
              </span>
              <h2 className="font-display mt-4 text-[24px] sm:text-[28px] font-bold tracking-[-0.02em] text-white">
                The setup
              </h2>
              <p className="mt-4 text-[15px] text-slate-300/90 leading-[1.75]">{c.summary}</p>
            </div>

            {/* Challenge */}
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A78BFA] backdrop-blur-md">
                The challenge
              </span>
              <h2 className="font-display mt-4 text-[24px] sm:text-[28px] font-bold tracking-[-0.02em] text-white">
                What was breaking
              </h2>
              <ul className="mt-5 space-y-3" data-testid={`case-study-${c.slug}-challenge`}>
                {c.challenge.map((ch) => (
                  <li key={ch} className="flex items-start gap-3 text-[14px] text-slate-200/90 leading-[1.7]">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#A78BFA] shrink-0" />
                    <span>{ch}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Approach */}
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
                The approach
              </span>
              <h2 className="font-display mt-4 text-[24px] sm:text-[28px] font-bold tracking-[-0.02em] text-white">
                What we changed
              </h2>
              <motion.div
                variants={stg}
                initial={initial}
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                className="mt-6 grid sm:grid-cols-2 gap-4"
                data-testid={`case-study-${c.slug}-approach`}
              >
                {c.approach.map((step, i) => (
                  <motion.div
                    key={step.title}
                    variants={up}
                    className="
                      rounded-2xl
                      bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                      border border-white/10
                      backdrop-blur-xl
                      p-5 h-full
                      transition-colors hover:border-[#7DDCFF]/30
                    "
                  >
                    <span className="font-mono text-[12px] text-[#22D3EE]">0{i + 1}</span>
                    <h3 className="mt-3 font-display text-[15.5px] font-semibold text-white">{step.title}</h3>
                    <p className="mt-2 text-[13.5px] text-slate-300/90 leading-[1.7]">{step.body}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Learnings */}
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#34D399] backdrop-blur-md">
                Key takeaways
              </span>
              <h2 className="font-display mt-4 text-[24px] sm:text-[28px] font-bold tracking-[-0.02em] text-white">
                What we would tell the next advertiser
              </h2>
              <ul className="mt-5 space-y-3" data-testid={`case-study-${c.slug}-learnings`}>
                {c.learnings.map((l) => (
                  <li key={l} className="flex items-start gap-3 text-[14px] text-slate-200/90 leading-[1.7]">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#34D399] shrink-0" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar: quote + recommended formats + related vertical */}
          <div className="lg:col-span-5 space-y-5">
            {c.quote && (
              <div className="
                relative rounded-2xl overflow-hidden
                bg-gradient-to-br from-[#0B172A]/95 to-[#050B17]/95
                border border-[#7DDCFF]/25
                backdrop-blur-xl
                p-6
                shadow-[0_30px_80px_rgba(0,0,0,0.55)]
              "
              data-testid={`case-study-${c.slug}-quote`}
              >
                <div
                  aria-hidden="true"
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(34,211,238,0.65), rgba(124,58,237,0.65), transparent)',
                  }}
                />
                <Quote className="h-6 w-6 text-[#22D3EE]" />
                <p className="mt-4 text-[15px] text-white/95 leading-[1.7] italic">“{c.quote.text}”</p>
                <p className="mt-4 text-[12.5px] text-slate-400">— {c.quote.attribution}</p>
              </div>
            )}

            {recommendedFormats.length > 0 && (
              <div className="rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur-xl p-6">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#A78BFA]">
                  Formats used
                </span>
                <h3 className="mt-2 font-display text-[17px] font-semibold text-white">Traffic formats in this campaign</h3>
                <ul className="mt-4 space-y-2.5" data-testid={`case-study-${c.slug}-formats`}>
                  {recommendedFormats.map((f) => (
                    <li key={f.slug}>
                      <Link
                        to={`/ad-formats/${f.slug}`}
                        className="
                          group flex items-start gap-3 rounded-xl
                          bg-white/[0.03] hover:bg-white/[0.06]
                          border border-white/10 hover:border-[#7DDCFF]/35
                          p-3 transition-colors
                        "
                      >
                        <span className="min-w-0">
                          <span className="block text-[13.5px] font-semibold text-white group-hover:text-[#7DDCFF] transition-colors">{f.title}</span>
                          <span className="block text-[12px] text-slate-400 mt-0.5 leading-snug">{f.short}</span>
                        </span>
                        <ArrowUpRight className="h-3.5 w-3.5 text-[#22D3EE] mt-0.5 shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {c.relatedVertical && (
              <div className="rounded-2xl bg-[#0B172A]/80 border border-white/10 backdrop-blur-xl p-6">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#7DDCFF]">
                  Related vertical
                </span>
                <h3 className="mt-2 font-display text-[17px] font-semibold text-white">
                  Explore {c.relatedVertical.label}
                </h3>
                <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">
                  See the full breakdown of traffic formats, compliance notes, and KPIs for this lead-gen vertical.
                </p>
                <Link
                  to={`/lead-generation/${c.relatedVertical.slug}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#22D3EE] hover:text-white transition-colors"
                  data-testid={`case-study-${c.slug}-related-vertical`}
                >
                  View {c.relatedVertical.label} vertical <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Other case studies */}
      {others.length > 0 && (
        <section className="relative bg-[#050B17] text-white isolate overflow-hidden">
          <DecorBackground variant="section" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-display text-[22px] sm:text-[26px] font-bold tracking-[-0.02em] text-white">
              More case studies
            </h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {others.map((o) => {
                const OIcon = o.icon;
                return (
                  <Link
                    key={o.slug}
                    to={`/case-studies/${o.slug}`}
                    data-testid={`case-study-${c.slug}-related-${o.slug}`}
                    className="
                      group flex items-start gap-4 rounded-2xl
                      bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                      border border-white/10 backdrop-blur-xl p-5
                      transition-colors hover:border-[#7DDCFF]/35
                    "
                  >
                    <span
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border shrink-0"
                      style={{
                        background: `linear-gradient(180deg, ${o.accent}26, ${o.accent}10)`,
                        borderColor: `${o.accent}55`,
                        color: o.accent,
                      }}
                    >
                      <OIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-display text-[15.5px] font-semibold text-white group-hover:text-[#7DDCFF] transition-colors">{o.title}</h3>
                      <p className="mt-1 text-[12.5px] text-slate-400 leading-relaxed line-clamp-2">{o.short}</p>
                      <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[#22D3EE]">
                        Read case study <ArrowUpRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CTASection
        eyebrow="Ready to apply?"
        title="Let’s get you some business"
        subtitle="Apply as advertiser. Your campaign reviewer will read your funnel, recommend a format mix and GEO plan, and set source-level rules before launch."
        primaryTo="/apply"
      />
    </>
  );
}
